import { api } from "../../api/client";

export interface Permission {
  id: string;
  resource: string;
  action: string;
  description: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  specialisations?: string[];
  isSystem: boolean;
}

export interface Position {
  id: string;
  name: string;
  description: string;
  scopeLevel: "district" | "constituency" | "ward" | "branch";
}

export interface AppUser {
  id: string;
  email: string;
  fullName: string;
  status: "active" | "disabled";
  permissions: string[];
  scope: { districtIds?: string[]; constituencyIds: string[]; wardIds: string[]; branchIds: string[] };
  specialisations?: string[];
  isSuperadmin?: boolean;
  /** Whether a facial-recognition second factor is enrolled for this officer. */
  mfaEnrolled?: boolean;
}

export interface Assignment {
  id: string;
  userId: string;
  roleId: string;
  positionId: string | null;
  scopeLevel: "district" | "constituency" | "ward" | "branch";
  scopeId: string;
  reportsToUid: string | null;
  startsAt: string | null;
  endsAt: string | null;
}

interface List<T> { items: T[]; }

export const RbacApi = {
  // Permissions catalogue is read directly from Firestore in Phase 2 via the
  // permissions doc collection. We expose it through /me/permissions for the
  // current user; for the role editor we list all permissions via Firestore
  // SDK because they're public-readable.
  // -- but to keep one auth path, we add this through the API too:
  listPermissions: async (): Promise<Permission[]> => {
    // Fall back to Firestore SDK read since it's public to signed-in users
    const { collection, getDocs } = await import("firebase/firestore");
    const { db } = await import("../../firebase.config");
    const snap = await getDocs(collection(db, "permissions"));
    return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Permission, "id">) }));
  },

  listRoles: () => api<List<Role>>("/roles"),
  createRole: (body: { id: string; name: string; description?: string; permissions: string[]; specialisations?: string[] }) =>
    api<Role>("/roles", { method: "POST", body: JSON.stringify(body) }),
  updateRole: (rid: string, body: Partial<{ name: string; description: string; permissions: string[] }>) =>
    api<Role>(`/roles/${rid}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteRole: (rid: string) =>
    api<void>(`/roles/${rid}`, { method: "DELETE" }),

  listPositions: () => api<List<Position>>("/positions"),
  createPosition: (body: { name: string; description?: string; scopeLevel: Position["scopeLevel"] }) =>
    api<Position>("/positions", { method: "POST", body: JSON.stringify(body) }),
  deletePosition: (pid: string) =>
    api<void>(`/positions/${pid}`, { method: "DELETE" }),

  listUsers: () => api<List<AppUser>>("/users"),
  inviteUser: (body: {
    email: string; fullName: string; password: string; phone?: string;
    specialisations?: string[];
    faceDescriptor?: number[] | null;
    faceImage?: string | null;
    faceImageContentType?: string | null;
  }) =>
    api<{ uid: string; email: string }>(
      "/users/invite",
      { method: "POST", body: JSON.stringify(body) },
    ),
  // Attach/replace an existing officer's enrolled face (admin-driven). The
  // photo is encrypted + stored server-side; only the descriptor matches.
  setUserFace: (uid: string, body: { faceDescriptor: number[]; faceImage?: string; faceImageContentType?: string }) =>
    api<{ id: string; faceEnrolled: boolean; hasImage: boolean }>(
      `/users/${uid}/face`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
  // Superadmin-only: fetch the decrypted enrolled photo as a data URL.
  getUserFaceImage: (uid: string) =>
    api<{ id: string; contentType: string; dataUrl: string }>(`/users/${uid}/face/image`),
  updateSpecialisations: (uid: string, specialisations: string[]) =>
    api<{ id: string; specialisations: string[] }>(
      `/users/${uid}/specialisations`,
      { method: "PATCH", body: JSON.stringify({ specialisations }) },
    ),
  deactivateUser: (uid: string) =>
    api<AppUser>(`/users/${uid}/deactivate`, { method: "POST" }),
  reactivateUser: (uid: string) =>
    api<AppUser>(`/users/${uid}/reactivate`, { method: "POST" }),
  resetPassword: (uid: string) =>
    api<{ passwordResetLink: string }>(`/users/${uid}/reset-password`, { method: "POST" }),
  grantSuperadmin: (uid: string) =>
    api<{ uid: string; role: string }>(`/users/${uid}/grant-superadmin`, { method: "POST" }),
  revokeSuperadmin: (uid: string) =>
    api<{ uid: string; role: string | null }>(`/users/${uid}/revoke-superadmin`, { method: "POST" }),

  listAssignments: (userId?: string) =>
    api<List<Assignment>>(`/assignments${userId ? `?userId=${userId}` : ""}`),
  createAssignment: (body: Omit<Assignment, "id">) =>
    api<Assignment>("/assignments", { method: "POST", body: JSON.stringify(body) }),
  deleteAssignment: (aid: string) =>
    api<void>(`/assignments/${aid}`, { method: "DELETE" }),
};
